import React from "react";
import ReactPaginate from "react-paginate";
import eventRefs from "../../assets/eventRef";
import { Link } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import axios from "axios";
import LoadingSpinner from "../../components/common/loading";
import {
  AppCard,
  AppPage,
  AppPageHeader,
  AppToolbar,
  AppToolbarField,
  AppToolbarGrid,
} from "../../components/common/appShell";
import {
  buildAlertCreatePath,
  buildAlertDetailsPath,
  buildAlertEditPath,
  getEventDescription,
} from "../../utils/deviceAlertUtils";
import "./deviceAlerts.css";

const EVENT_REF_MAP = new Map(eventRefs.map((eventRef) => [String(eventRef.code), eventRef]));
const DEFAULT_PAGE_SIZE = 25;
const CREATE_DEVICE_SEARCH_LIMIT = 10;

const EMPTY_SUMMARY = {
  totalDevices: 0,
  onlineDevices: 0,
  totalAlerts: 0,
  activeAlerts: 0,
  clearAlerts: 0,
  affectedDevices: 0,
  affectedActiveDevices: 0,
  triggeredAlerts: 0,
  triggeredDevices: 0,
  clearMonitoredAlerts: 0,
  triggersFired24h: 0,
  offlineEvents: 0,
  offlineDevices: 0,
  actionsAttached: 0,
  alertsWithoutActions: 0,
};

const STATUS_FILTER_OPTIONS = [
  { label: "All alerts", value: "", countKey: "totalAlerts" },
  { label: "Triggered", value: "Alert", countKey: "activeAlerts" },
  { label: "Clear", value: "OK", countKey: "clearAlerts" },
];

const SORT_OPTIONS = [
  { label: "Needs attention", value: "" },
  { label: "Alert name A-Z", value: "eventName_asc" },
  { label: "Alert name Z-A", value: "eventName_desc" },
  { label: "Recently triggered", value: "lastTriggered_desc" },
  { label: "Oldest triggered", value: "lastTriggered_asc" },
];

const isAdminUser = (user = {}) =>
  Boolean(user?.is_enterprise_admin || user?.has_admin_privileges);

const canUseAnyRolePermission = (user = {}, resources = {}, field) => {
  if (!user || Object.keys(user).length === 0) {
    return true;
  }

  if (isAdminUser(user)) {
    return true;
  }

  const roles = [
    user?.role,
    ...(Array.isArray(resources?.groups)
      ? resources.groups.map((group) => group?.role)
      : []),
  ].filter(Boolean);

  if (!user?.role && !resources?.groupsLoaded) {
    return false;
  }

  if (roles.length === 0) {
    return true;
  }

  return roles.some((role) => role?.[field] === true);
};

const SUMMARY_TILE_COLORS = {
  danger: "#ef4444",
  success: "#22c55e",
  warning: "#f59e0b",
  neutral: "#64748b",
  info: "#3b82f6",
};

const compareStrings = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    sensitivity: "base",
    numeric: true,
  });

const toNumber = (value, fallback = 0) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const getEventRefByCode = (code) => EVENT_REF_MAP.get(String(code)) || null;

const buildEventNameOptions = (codes = []) => {
  if (!Array.isArray(codes)) {
    return [];
  }

  const uniqueCodes = Array.from(
    new Set(
      codes
        .map((code) => Number(code))
        .filter((code) => Number.isFinite(code)),
    ),
  );

  return uniqueCodes
    .map((code) => {
      const label = getEventRefByCode(code)?.name || `Event Code ${code}`;

      return {
        value: String(code),
        label,
      };
    })
    .sort((a, b) => compareStrings(a.label, b.label));
};

const getDeviceSerial = (device) =>
  device?.enclosureSerialNumber ||
  device?.enclosure_sn ||
  device?.enclosureSN ||
  device?.serial ||
  "";

const getDeviceName = (device) => device?.deviceName || device?.name || "PDU";

const getEventDeviceName = (event = {}) => {
  if (event.deviceName && event.deviceName !== "SynLink PDU") {
    return event.deviceName;
  }

  return event.enclosureSerialNumber ? `PDU SN:${event.enclosureSerialNumber}` : "PDU";
};

const getActionCount = (actions) => {
  if (Array.isArray(actions)) {
    return actions.length;
  }

  if (typeof actions === "string") {
    try {
      const parsedActions = JSON.parse(actions);
      return Array.isArray(parsedActions) ? parsedActions.length : 0;
    } catch (_error) {
      return 0;
    }
  }

  return 0;
};

const getAlertCategory = (eventRefName = "") => {
  const normalizedName = String(eventRefName).toLowerCase();

  if (normalizedName.startsWith("inlet") || normalizedName.startsWith("bank")) {
    return "Power";
  }

  if (normalizedName.startsWith("outlet")) {
    return "Outlet";
  }

  if (
    normalizedName.includes("temperature") ||
    normalizedName.includes("humidity") ||
    normalizedName.includes("dewpoint")
  ) {
    return "Environmental";
  }

  if (
    normalizedName.includes("sensor") ||
    normalizedName.includes("digital") ||
    normalizedName.includes("switch")
  ) {
    return "Sensor / IO";
  }

  if (
    normalizedName.includes("network") ||
    normalizedName.includes("ping") ||
    normalizedName.includes("ethernet")
  ) {
    return "Network";
  }

  if (normalizedName.includes("schedule")) {
    return "Scheduled";
  }

  if (normalizedName.includes("ats")) {
    return "ATS";
  }

  return "Alert";
};

const toDateFromEpochSeconds = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(Number(value) * 1000);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLastTriggered = (event = {}) => {
  const lastTriggered = toDateFromEpochSeconds(event.timeExecutedInEpoch);

  if (event.triggered) {
    return {
      prominent: lastTriggered
        ? `Active ${formatDistanceToNow(lastTriggered)}`
        : "Triggered now",
      secondary: lastTriggered ? format(lastTriggered, "MMM d, h:mm a") : "Awaiting timestamp",
    };
  }

  if (!lastTriggered) {
    return {
      prominent: "Never triggered",
      secondary: "No trigger history yet",
    };
  }

  return {
    prominent: `Last triggered ${formatDistanceToNow(lastTriggered, { addSuffix: true })}`,
    secondary: format(lastTriggered, "MMM d, h:mm a"),
  };
};

const formatLastSync = (event = {}) => {
  const lastSyncDate = toDate(event.lastChange);

  if (!lastSyncDate) {
    return {
      prominent: "Last sync unavailable",
      secondary: "Awaiting device state",
    };
  }

  return {
    prominent: `Last sync ${formatDistanceToNow(lastSyncDate, { addSuffix: true })}`,
    secondary: format(lastSyncDate, "MMM d, h:mm a"),
  };
};

const getAlertDescription = (event = {}) => {
  const description = getEventDescription(event);
  return description || "No condition details are available for this alert.";
};

const getSummaryFallback = (events = [], totalCount = 0) => {
  const activeAlerts = events.filter((event) => Boolean(event?.triggered)).length;
  const clearMonitoredEvents = events.filter(
    (event) =>
      !Boolean(event?.triggered) && event?.connectionState !== "Disconnected",
  );
  const recentTriggerThreshold = Math.floor(Date.now() / 1000) - 24 * 60 * 60;
  const offlineEvents = events.filter(
    (event) => event?.connectionState === "Disconnected",
  ).length;
  const affectedDevices = new Set(
    events
      .map((event) => event?.enclosureSerialNumber)
      .filter((serial) => serial != null && serial !== ""),
  ).size;
  const onlineDevices = new Set(
    events
      .filter((event) => event?.connectionState !== "Disconnected")
      .map((event) => event?.enclosureSerialNumber)
      .filter((serial) => serial != null && serial !== ""),
  ).size;
  const affectedActiveDevices = new Set(
    events
      .filter((event) => Boolean(event?.triggered))
      .map((event) => event?.enclosureSerialNumber)
      .filter((serial) => serial != null && serial !== ""),
  ).size;
  const offlineDevices = new Set(
    events
      .filter((event) => event?.connectionState === "Disconnected")
      .map((event) => event?.enclosureSerialNumber)
      .filter((serial) => serial != null && serial !== ""),
  ).size;
  const triggersFired24h = events.filter((event) => {
    const lastTriggeredEpoch = toNumber(event?.timeExecutedInEpoch, 0);
    return lastTriggeredEpoch >= recentTriggerThreshold;
  }).length;

  const totalAlerts = totalCount || events.length;

  return {
    ...EMPTY_SUMMARY,
    totalDevices: affectedDevices,
    onlineDevices,
    totalAlerts,
    activeAlerts,
    clearAlerts: Math.max(0, totalAlerts - activeAlerts),
    affectedDevices,
    affectedActiveDevices,
    triggeredAlerts: activeAlerts,
    triggeredDevices: affectedActiveDevices,
    clearMonitoredAlerts: clearMonitoredEvents.length,
    triggersFired24h,
    offlineEvents,
    offlineDevices,
    actionsAttached: events.filter((event) => getActionCount(event?.actions) > 0).length,
    alertsWithoutActions: events.filter((event) => getActionCount(event?.actions) === 0).length,
  };
};

const normalizeSummary = (summary, events = [], totalCount = 0) => {
  if (!summary || typeof summary !== "object") {
    return getSummaryFallback(events, totalCount);
  }

  const normalized = Object.keys(EMPTY_SUMMARY).reduce((nextSummary, key) => {
    nextSummary[key] = toNumber(summary[key], EMPTY_SUMMARY[key]);
    return nextSummary;
  }, {});

  if (summary.triggeredAlerts == null) {
    normalized.triggeredAlerts = normalized.activeAlerts;
  }
  if (summary.triggeredDevices == null) {
    normalized.triggeredDevices = normalized.affectedActiveDevices;
  }
  if (summary.clearMonitoredAlerts == null) {
    normalized.clearMonitoredAlerts = normalized.clearAlerts;
  }

  return normalized;
};

const AlertStatusPill = ({ children, tone }) => (
  <span className={`fleet-alerts__pill fleet-alerts__pill--${tone}`}>
    {children}
  </span>
);

const SummaryTile = ({ label, value, meta, tone = "neutral" }) => (
  <div className="app-kpi-card fleet-alerts__summary-tile">
    <div className="app-kpi-card__value">{value}</div>
    <div className="app-kpi-card__label">{label}</div>
    {meta ? <div className="app-kpi-card__meta">{meta}</div> : null}
    <div
      className="app-kpi-card__accent"
      style={{ background: SUMMARY_TILE_COLORS[tone] || SUMMARY_TILE_COLORS.neutral }}
    />
  </div>
);

const AlertRowSkeleton = ({ index }) => (
  <article className="fleet-alerts__row fleet-alerts__row--loading" key={index}>
    <div className="fleet-alerts__skeleton-pill skeleton-blank" />
    <div className="fleet-alerts__row-main">
      <div className="fleet-alerts__skeleton-line skeleton-blank" />
      <div className="fleet-alerts__skeleton-line fleet-alerts__skeleton-line--short skeleton-blank" />
    </div>
    <div className="fleet-alerts__skeleton-block skeleton-blank" />
    <div className="fleet-alerts__skeleton-block skeleton-blank" />
    <div className="fleet-alerts__skeleton-block skeleton-blank" />
  </article>
);

export default class DeviceAlerts extends React.Component {
  state = {
    searchBar: "",
    currentFilter: "",
    eventNameFilter: "",
    eventTriggeredFilter: "",
    sortFilter: "",

    events: [],
    eventsLoading: false,
    refreshing: new Set(),
    page: 1,
    totalPages: 0,
    totalCount: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    eventNameOptions: [],
    summary: EMPTY_SUMMARY,

    createModalOpen: false,
    deviceSearch: "",
    deviceResults: [],
    devicesLoading: false,
    selectedDevice: null,
    deviceSearchError: "",
  };

  componentDidMount() {
    this.fetchEvents({ page: 1 });
    this.maybeFetchGroups();
  }

  componentDidUpdate(prevProps) {
    const prevQuery = prevProps.queryParams || {};
    const nextQuery = this.props.queryParams || {};

    const prevKeys = Object.keys(prevQuery);
    const nextKeys = Object.keys(nextQuery);

    const queryChanged =
      prevKeys.length !== nextKeys.length ||
      prevKeys.some((key) => prevQuery[key] !== nextQuery[key]);

    if (queryChanged) {
      this.setState(
        {
          page: 1,
        },
        () => {
          this.fetchEvents({ page: 1 });
        },
      );
    }

    if (
      prevProps.resources?.groupsLoaded !== this.props.resources?.groupsLoaded ||
      prevProps.resources?.groupsLoading !== this.props.resources?.groupsLoading
    ) {
      this.maybeFetchGroups();
    }
  }

  maybeFetchGroups = () => {
    const resources = this.props.resources || {};
    if (
      this.props.fetchResource &&
      !resources.groupsLoaded &&
      !resources.groupsLoading
    ) {
      this.props.fetchResource("groups");
    }
  };

  applyFilters = () => {
    this.setState(
      {
        currentFilter: this.state.searchBar,
        page: 1,
      },
      () => {
        this.fetchEvents({ page: 1 });
      },
    );
  };

  fetchEvents = ({ page } = {}) => {
    const nextPage = page || this.state.page || 1;

    this.setState({
      eventsLoading: true,
    });

    const params = new URLSearchParams();
    const { queryParams } = this.props;

    Object.entries(queryParams || {}).forEach(([key, value]) => {
      if (value == null || value === "") {
        return;
      }

      params.append(key, value);
    });

    params.set("page", nextPage);
    params.set("limit", this.state.pageSize);

    if (this.state.sortFilter) {
      params.set("sort", this.state.sortFilter);
    }

    if (this.state.currentFilter) {
      params.set("search", this.state.currentFilter);
    }

    if (this.state.eventNameFilter) {
      params.set("code", this.state.eventNameFilter);
    }

    if (this.state.eventTriggeredFilter === "Alert") {
      params.set("triggered", "true");
    } else if (this.state.eventTriggeredFilter === "OK") {
      params.set("triggered", "false");
    }

    const url = `/v3/events?${params.toString()}`;

    axios
      .get(url, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      })
      .then((response) => {
        const payload = response?.data || {};
        const events = Array.isArray(payload.data) ? payload.data : [];
        const meta = payload.meta || {};
        let eventNameOptions = buildEventNameOptions(meta.eventCodes || []);

        if (this.state.eventNameFilter) {
          const hasSelected = eventNameOptions.some(
            (option) => option.value === this.state.eventNameFilter,
          );

          if (!hasSelected) {
            const numericCode = Number(this.state.eventNameFilter);
            eventNameOptions = [
              ...eventNameOptions,
              {
                value: this.state.eventNameFilter,
                label:
                  getEventRefByCode(numericCode)?.name ||
                  `Event Code ${this.state.eventNameFilter}`,
              },
            ].sort((a, b) => compareStrings(a.label, b.label));
          }
        }

        this.setState({
          events,
          eventsLoading: false,
          page: meta.currentPage || nextPage,
          totalPages: meta.totalPages || 0,
          totalCount: meta.totalCount || 0,
          eventNameOptions,
          summary: normalizeSummary(
            meta.statusSummary || meta.summary,
            events,
            meta.totalCount || 0,
          ),
        });
      })
      .catch((error) => {
        console.log("Error fetching events", error);
        this.setState({
          eventsLoading: false,
        });
      });
  };

  openCreateModal = () => {
    if (!this.canEditEvents()) {
      return;
    }

    this.setState({
      createModalOpen: true,
      deviceSearch: "",
      deviceResults: [],
      devicesLoading: false,
      selectedDevice: null,
      deviceSearchError: "",
    });
  };

  closeCreateModal = () => {
    this.setState({
      createModalOpen: false,
      devicesLoading: false,
      deviceSearchError: "",
    });
  };

  canEditEvents = () =>
    canUseAnyRolePermission(
      this.props.user,
      this.props.resources,
      "editEventInfo",
    );

  searchDevices = async () => {
    const query = (this.state.deviceSearch || "").trim();
    if (!query) {
      this.setState({ deviceSearchError: "Enter a name or serial to search." });
      return;
    }

    this.setState({ devicesLoading: true, deviceSearchError: "" });

    try {
      const params = new URLSearchParams({
        page: "1",
        limit: String(CREATE_DEVICE_SEARCH_LIMIT),
        search: query,
      });
      const groupId = this.props.queryParams?.groupId;

      if (groupId != null && groupId !== "") {
        params.set("groupId", String(groupId));
      }

      const response = await axios.get(`/v2/devices?${params.toString()}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];

      this.setState({
        deviceResults: data,
        devicesLoading: false,
        selectedDevice: data[0] || null,
        deviceSearchError: data.length ? "" : "No devices found.",
      });
    } catch (_error) {
      this.setState({
        devicesLoading: false,
        deviceSearchError: "Unable to search devices. Please try again.",
      });
    }
  };

  handleSelectDevice = (device) => {
    this.setState({ selectedDevice: device });
  };

  goToCreateAlert = () => {
    const serial = getDeviceSerial(this.state.selectedDevice);
    if (!serial) {
      return;
    }

    this.props.history.push(buildAlertCreatePath(serial));
    this.closeCreateModal();
  };

  handleStatusFilter = (value) => {
    this.setState(
      {
        eventTriggeredFilter: value,
        page: 1,
      },
      () => {
        this.fetchEvents({ page: 1 });
      },
    );
  };

  refreshEvent = (enclosureSerialNumber) => {
    if (this.state.refreshing.has(enclosureSerialNumber)) return;

    this.setState((prevState) => ({
      refreshing: new Set(prevState.refreshing).add(enclosureSerialNumber),
    }));

    this.props.getDeviceItemAndData(
      enclosureSerialNumber,
      (err) => {
        if (err) {
          console.log("Error getting device item and data", err);
        }

        this.setState(
          (prevState) => {
            const nextRefreshing = new Set(prevState.refreshing);
            nextRefreshing.delete(enclosureSerialNumber);
            return { refreshing: nextRefreshing };
          },
          () => {
            if (!err) {
              this.fetchEvents({ page: this.state.page });
            }
          },
        );
      },
      enclosureSerialNumber,
    );
  };

  renderAlertRow = (event, index) => {
    const serial = event?.enclosureSerialNumber || "";
    const eventRef = getEventRefByCode(event?.code);
    const eventRefName = eventRef?.name || `Event ${event?.code ?? ""}`;
    const actionCount = getActionCount(event?.actions);
    const isOffline = event?.connectionState === "Disconnected";
    const timing = formatLastTriggered(event);
    const syncTiming = formatLastSync(event);
    const detailPath = buildAlertDetailsPath(serial, event?.id);
    const editPath = buildAlertEditPath(serial, event?.id);
    const eventKey = [serial || "unknown", event?.id ?? index].join("::");
    const canEditEvents = this.canEditEvents();

    return (
      <article
        key={eventKey}
        className={[
          "fleet-alerts__row",
          event?.triggered ? "fleet-alerts__row--triggered" : "",
          isOffline ? "fleet-alerts__row--offline" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="fleet-alerts__row-status">
          <AlertStatusPill tone={event?.triggered ? "danger" : "success"}>
            {event?.triggered ? "Triggered" : "Clear"}
          </AlertStatusPill>
          {isOffline ? <AlertStatusPill tone="warning">Device offline</AlertStatusPill> : null}
        </div>

        <div className="fleet-alerts__row-main">
          <Link to={detailPath} className="fleet-alerts__row-title">
            {event?.name || eventRefName || "Unnamed alert"}
          </Link>
          <div className="fleet-alerts__row-summary">{getAlertDescription(event)}</div>
          <div className="fleet-alerts__row-tags">
            <span>{eventRefName}</span>
            <span>{getAlertCategory(eventRefName)}</span>
            <span>
              {actionCount > 0
                ? `${actionCount} action${actionCount === 1 ? "" : "s"} attached`
                : "No actions attached"}
            </span>
          </div>
        </div>

        <div className="fleet-alerts__row-device">
          <Link to={`/devices/connected/${serial}`} className="fleet-alerts__device-link">
            {getEventDeviceName(event)}
          </Link>
          <div className="fleet-alerts__row-muted">SN: {serial || "Unknown"}</div>
        </div>

        <div className="fleet-alerts__row-time">
          <div className="fleet-alerts__row-time-prominent">{timing.prominent}</div>
          <div className="fleet-alerts__row-muted">{timing.secondary}</div>
        </div>

        <div className="fleet-alerts__row-sync">
          <div className="fleet-alerts__row-sync-prominent">{syncTiming.prominent}</div>
          <div className="fleet-alerts__row-muted">{syncTiming.secondary}</div>
          {this.state.refreshing.has(serial) ? (
            <div className="fleet-alerts__refresh-inline">
              <LoadingSpinner black />
            </div>
          ) : (
            <button
              type="button"
              className="fleet-alerts__refresh-button"
              title="Refresh PDU information"
              onClick={() => this.refreshEvent(serial)}
            >
              <span>Refresh</span>
            </button>
          )}
        </div>

        <div className="fleet-alerts__row-actions">
          <Link to={detailPath} className="fleet-alerts__row-action">
            View details
          </Link>
          {canEditEvents ? (
            <Link
              to={editPath}
              className="fleet-alerts__row-action fleet-alerts__row-action--muted"
            >
              Edit alert
            </Link>
          ) : null}
        </div>
      </article>
    );
  };

  renderAlertHeader = () => (
    <div className="fleet-alerts__row-header" aria-hidden="true">
      <div>Status</div>
      <div>Alert</div>
      <div>Device</div>
      <div>Last triggered</div>
      <div>Last sync</div>
      <div>Actions</div>
    </div>
  );

  renderAlertSection = ({ title, events }) => {
    if (!events.length) {
      return null;
    }

    return (
      <section className="fleet-alerts__section">
        <div className="fleet-alerts__section-heading">
          <span>{title}</span>
          <span className="fleet-alerts__section-count">{events.length}</span>
        </div>
        {this.renderAlertHeader()}
        <div className="fleet-alerts__rows">
          {events.map((event, index) => this.renderAlertRow(event, index))}
        </div>
      </section>
    );
  };

  renderCreateModal = () => {
    if (!this.state.createModalOpen) {
      return null;
    }

    return (
      <div className="fleet-alerts__modal-backdrop" role="dialog" aria-modal="true">
        <div className="fleet-alerts__modal">
          <div className="fleet-alerts__modal-header">
            <div>
              <h3 className="fleet-alerts__modal-title">Create an alert</h3>
              <p className="fleet-alerts__modal-copy">
                Pick a PDU to create a new alert for that device.
              </p>
            </div>
            <button
              type="button"
              onClick={this.closeCreateModal}
              className="fleet-alerts__modal-close"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <div className="fleet-alerts__modal-body">
            <label className="fleet-alerts__modal-label" htmlFor="device-search">
              Search by name or serial
            </label>
            <div className="fleet-alerts__device-search">
              <input
                id="device-search"
                type="text"
                value={this.state.deviceSearch}
                onChange={(event) =>
                  this.setState({ deviceSearch: event.target.value })
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") this.searchDevices();
                }}
                placeholder="e.g. SJC-Row3-PDU4 or serial 1508993"
              />
              <button
                className="button"
                type="button"
                onClick={this.searchDevices}
                disabled={this.state.devicesLoading}
              >
                {this.state.devicesLoading ? "Searching..." : "Search"}
              </button>
            </div>
            {this.state.deviceSearchError && (
              <div className="fleet-alerts__modal-error">
                {this.state.deviceSearchError}
              </div>
            )}

            <div className="fleet-alerts__device-results">
              {this.state.devicesLoading ? (
                <div className="fleet-alerts__device-results-empty">Loading devices...</div>
              ) : (
                <div className="fleet-alerts__device-results-list">
                  {(this.state.deviceResults || []).map((device) => {
                    const serial = getDeviceSerial(device);
                    const selected =
                      serial && serial === getDeviceSerial(this.state.selectedDevice);

                    return (
                      <button
                        key={serial || getDeviceName(device)}
                        type="button"
                        onClick={() => this.handleSelectDevice(device)}
                        className={[
                          "fleet-alerts__device-result",
                          selected ? "fleet-alerts__device-result--selected" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <div>
                          <div className="fleet-alerts__device-result-name">
                            {getDeviceName(device)}
                          </div>
                          <div className="fleet-alerts__device-result-serial">
                            SN: {serial || "Unknown"}
                          </div>
                        </div>
                        <span className="fleet-alerts__device-result-state">
                          {selected ? "Selected" : "Select"}
                        </span>
                      </button>
                    );
                  })}
                  {(this.state.deviceResults || []).length === 0 &&
                    !this.state.devicesLoading &&
                    !this.state.deviceSearchError && (
                      <div className="fleet-alerts__device-results-empty">
                        Start a search to find a PDU.
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
          <div className="fleet-alerts__modal-footer">
            <button
              type="button"
              className="button secondary"
              onClick={this.closeCreateModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button"
              onClick={this.goToCreateAlert}
              disabled={!getDeviceSerial(this.state.selectedDevice)}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      events,
      eventsLoading,
      sortFilter,
      eventNameOptions,
      page,
      totalPages,
      totalCount,
      pageSize,
      summary,
    } = this.state;
    const isDeviceScoped = Boolean(this.props.match?.params?.enclosureSerialNumber);
    const isGroupScoped = Boolean(this.props.queryParams?.groupId);
    const canEditEvents = this.canEditEvents();
    const currentPage = page || 1;
    const summaryData = normalizeSummary(summary, events, totalCount);
    const connectedTriggeredAlerts = events.filter(
      (event) => Boolean(event?.triggered) && event?.connectionState !== "Disconnected",
    );
    const offlineAlerts = events.filter(
      (event) => event?.connectionState === "Disconnected",
    );
    const clearAlerts = events.filter(
      (event) => !event?.triggered && event?.connectionState !== "Disconnected",
    );

    let summaryText = null;
    if (!(eventsLoading && events.length === 0)) {
      if (typeof totalCount === "number") {
        if (totalCount === 0) {
          summaryText = "Showing 0 of 0 alerts";
        } else if (events.length === 0) {
          summaryText = `Showing 0 of ${totalCount} alerts`;
        } else {
          const showingStart = (currentPage - 1) * pageSize + 1;
          const showingEnd = Math.min(showingStart + events.length - 1, totalCount);
          summaryText = `Showing ${showingStart}-${showingEnd} of ${totalCount} alerts`;
        }
      }
    }

    const headerTitle = isDeviceScoped
      ? "Device alerts"
      : isGroupScoped
        ? "Group alerts"
        : "Alerts";
    const headerDescription = isDeviceScoped
      ? "Review configured alerts for this device, check which ones are active, and jump into event history."
      : isGroupScoped
        ? "Prioritize active alerts in this device group, inspect affected PDUs, and open the matching alert history."
        : "Prioritize active alerts across your fleet, inspect affected PDUs, and open the matching alert history.";

    const triggeredAlerts =
      summaryData.triggeredAlerts ?? summaryData.activeAlerts;
    const triggeredDevices =
      summaryData.triggeredDevices ?? summaryData.affectedActiveDevices;
    const clearMonitoredAlerts =
      summaryData.clearMonitoredAlerts ?? summaryData.clearAlerts;

    const summaryTiles = [
      {
        key: "online-devices",
        label: "Online Devices",
        value: summaryData.onlineDevices,
        meta: `${summaryData.totalDevices} total device${
          summaryData.totalDevices === 1 ? "" : "s"
        }`,
        tone: summaryData.onlineDevices > 0 ? "success" : "neutral",
      },
      {
        key: "active",
        label: "Active Alerts",
        value: triggeredAlerts,
        meta: `${triggeredDevices} affected device${
          triggeredDevices === 1 ? "" : "s"
        }`,
        tone: triggeredAlerts > 0 ? "danger" : "success",
      },
      {
        key: "clear",
        label: "Monitoring Normally",
        value: clearMonitoredAlerts,
        meta: "Configured alerts not triggered",
        tone: "neutral",
      },
      {
        key: "triggers-fired-24h",
        label: "Triggers Fired (24h)",
        value: summaryData.triggersFired24h,
        meta: "Last triggered within 24 hours",
        tone: summaryData.triggersFired24h > 0 ? "danger" : "neutral",
      },
    ];

    return (
      <AppPage className="fleet-alerts">
        <AppPageHeader
          title={headerTitle}
          description={headerDescription}
          actions={
            <React.Fragment>
              <Link to="/alerts-documentation" style={{ color: "inherit" }}>
                <div className="button secondary fleet-alerts__header-button">
                  <i className="material-icons">help</i>
                  <span>Alert documentation</span>
                </div>
              </Link>
              {canEditEvents ? (
                isDeviceScoped ? (
                  <Link
                    to={buildAlertCreatePath(this.props.match.params.enclosureSerialNumber)}
                    style={{ color: "inherit" }}
                  >
                    <div className="button fleet-alerts__header-button">
                      <i aria-hidden="true" className="material-icons">add</i>
                      <span>Create alert</span>
                    </div>
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="button fleet-alerts__header-button"
                    onClick={this.openCreateModal}
                  >
                    <i aria-hidden="true" className="material-icons">add</i>
                    <span>Create alert</span>
                  </button>
                )
              ) : null}
            </React.Fragment>
          }
        />

        <section className="app-kpi-grid fleet-alerts__summary-grid">
          {summaryTiles.map((tile) => (
            <SummaryTile
              key={tile.key}
              label={tile.label}
              value={tile.value}
              meta={tile.meta}
              tone={tile.tone}
            />
          ))}
        </section>

        <section className="fleet-alerts__status-filters" aria-label="Alert status filters">
          {STATUS_FILTER_OPTIONS.map((option) => {
            const isActive = this.state.eventTriggeredFilter === option.value;
            const count = summaryData[option.countKey] || 0;

            return (
              <button
                key={option.value || "all"}
                type="button"
                className={[
                  "fleet-alerts__status-filter",
                  isActive ? "fleet-alerts__status-filter--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-pressed={isActive}
                onClick={() => this.handleStatusFilter(option.value)}
              >
                <span>{option.label}</span>
                <span className="fleet-alerts__status-filter-count">{count}</span>
              </button>
            );
          })}
        </section>

        <AppToolbar variant="plain" className="fleet-alerts__toolbar">
          <AppToolbarGrid wide>
            <AppToolbarField label="Search alerts" grow>
              <div className="app-toolbar__search">
                <input
                  type="text"
                  placeholder="Search by device name, serial number, alert name, or parameter"
                  value={this.state.searchBar}
                  onChange={(event) => {
                    this.setState({ searchBar: event.target.value });
                  }}
                  onKeyPress={(event) => {
                    if (event.key === "Enter") {
                      this.applyFilters();
                    }
                  }}
                />
                <span
                  className="search-icon-button"
                  onClick={() => {
                    this.applyFilters();
                  }}
                >
                  <i className="material-icons">search</i>
                </span>
              </div>
            </AppToolbarField>
            <AppToolbarField label="Alert type">
              <select
                onChange={(event) => {
                  const value = event.target.value;
                  this.setState(
                    {
                      eventNameFilter: value,
                      page: 1,
                    },
                    () => {
                      this.fetchEvents({ page: 1 });
                    },
                  );
                }}
                value={this.state.eventNameFilter}
              >
                <option value="">All alert types</option>
                {eventNameOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </AppToolbarField>
            <AppToolbarField label="Sort by">
              <select
                value={sortFilter}
                onChange={(event) => {
                  const value = event.target.value;
                  this.setState(
                    {
                      sortFilter: value,
                      page: 1,
                    },
                    () => {
                      this.fetchEvents({ page: 1 });
                    },
                  );
                }}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value || "default"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </AppToolbarField>
          </AppToolbarGrid>
        </AppToolbar>

        <AppCard variant="hybrid-list" className="fleet-alerts__worklist">
          {summaryText ? <div className="fleet-alerts__summary-text">{summaryText}</div> : null}

          {eventsLoading ? (
            <div className="fleet-alerts__rows">
              {[0, 1, 2, 3].map((index) => (
                <AlertRowSkeleton key={index} index={index} />
              ))}
            </div>
          ) : events.length === 0 ? (
            <section className="app-empty-state fleet-alerts__empty-state">
              <h3>No alerts found</h3>
              <p>
                Adjust the search, type, or status filters to bring alerts back into view.
              </p>
            </section>
          ) : (
            <React.Fragment>
              {this.renderAlertSection({
                title: "Triggered Now",
                events: connectedTriggeredAlerts,
              })}
              {this.renderAlertSection({
                title: "Offline Device State",
                events: offlineAlerts,
              })}
              {this.renderAlertSection({
                title: "Monitoring / Clear",
                events: clearAlerts,
              })}
            </React.Fragment>
          )}
        </AppCard>

        {totalPages > 1 && (
          <div className="fleet-alerts__pagination">
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              breakLabel="..."
              forcePage={Math.max(0, (page || 1) - 1)}
              pageCount={totalPages}
              onPageChange={({ selected }) => {
                this.fetchEvents({ page: selected + 1 });
              }}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              containerClassName="react-paginate"
              activeClassName="selected"
            />
          </div>
        )}

        {this.renderCreateModal()}
      </AppPage>
    );
  }
}
